<template>
    <tabs :options="{ useUrlFragment: false }">
        <tab
            name="Dashboard"
            panel-class="backlog-insights-section"
        >
            <section
                aria-labelledby="workstream-charts-heading"
                class="charts-container"
            >
                <h2
                    id="workstream-charts-heading"
                    class="visually-hidden"
                >
                    Workstream Charts
                </h2>
                <OperationsInsightsChart
                    title="Backlog by Workstream"
                    :chart-options="backlogOpenByWorkstreamChartOptions"
                    :is-loading="isLoading"
                    :error="error"
                />
                <OperationsInsightsChart
                    title="Backlog In / Out by Workstream"
                    :chart-options="backlogInOutAttrByWorkstreamChartOptions"
                    :is-loading="isLoading"
                    :error="error"
                />
            </section>

            <section
                aria-labelledby="workstream-grids-heading"
                class="grid-container-wrapper"
            >
                <h2
                    id="workstream-grids-heading"
                    class="visually-hidden"
                >
                    Workstream Data
                </h2>
                <div class="grid-container">
                    <OperationsInsightsGrid
                        title="Backlog Health by Workstream"
                        :column-defs="backlogHealthByWorkstreamColumnDefs"
                        :row-data="backlogHealthByWorkstreamGridData"
                        :error="error"
                    />
                </div>
                <div class="grid-container">
                    <OperationsInsightsGrid
                        title="Backlog Trend by Workstream"
                        :column-defs="backlogTrendByWorkstreamColumnDefs"
                        :row-data="backlogTrendByWorkstreamGridData"
                        :error="error"
                    />
                </div>
            </section>

            <section
                aria-labelledby="interval-grids-heading"
                class="grid-container-wrapper"
            >
                <h2
                    id="interval-grids-heading"
                    class="visually-hidden"
                >
                    Interval Data
                </h2>

                <div class="grid-container">
                    <OperationsInsightsGrid
                        title="Backlog Age by Workstream"
                        :column-defs="backlogAgeByWorkstreamColumnDefs"
                        :row-data="backlogAgeByWorkstreamGridData"
                        :error="blAgeBandsWorkstreamsError"
                    />
                </div>
            </section>
            <section class="grid-container-wrapper">
                <div class="grid-container">
                    <OperationsInsightsGrid
                        title="Workstream Summary"
                        :column-defs="workstreamSummaryColumnDefs"
                        :row-data="workstreamSummaryGridData"
                        :error="workstreamSummaryError"
                    />
                </div>
            </section>
        </tab>
        <tab
            name="Explorer"
            panel-class="backlog-insights-section"
        >
            <BacklogInsightsExplorerTab
                v-model:is-enabled="isExplorerEnabled"
                :query-params="queryParams"
            />
        </tab>
    </tabs>
</template>

<script setup lang="ts">
import OperationsInsightsChart from '@/pages/OperationsInsights/components/OperationsInsightsChart.vue';
import OperationsInsightsGrid from '@/pages/OperationsInsights/components/OperationsInsightsGrid.vue';
import { ApiQueryParams } from '@/pages/OperationsInsights/types/ApiQueryParams';
import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';
import {
    ref, toRef, watch
} from 'vue';
import { useBLAgeBandsWorkstreamsData } from '@/pages/OperationsInsights/composables/useBLAgeBandsWorkstreamsData';
import { useBLWorkstreamSummaryData } from '@/pages/OperationsInsights/composables/useBLWorkstreamSummaryData';
import { useBLAnalysisChartConfigs } from '@/pages/OperationsInsights/composables/useBLAnalysisChartConfigs';
import { useCombinedStates } from '@/pages/OperationsInsights/composables/useCombinedStates';
import { Tabs, Tab } from '@agyletime/vue-3-common/src/components/Tabs';
import BacklogInsightsExplorerTab from '@/pages/OperationsInsights/components/BacklogInsightsExplorerTab.vue';

type Props = {
    queryParams: ApiQueryParams;
};
const props = defineProps<Props>();

const logger = useLogger('BacklogInsightsTab');
const queryParamsRef = toRef(props, 'queryParams');

const isExplorerEnabled = ref(false);

watch(
    queryParamsRef,
    (newParams) => {
        logger.debug('queryParams changed', newParams);
    },
    { deep: true, immediate: true }
);

const blAgeBandsWorkstreamsState = useBLAgeBandsWorkstreamsData(queryParamsRef);
const {
    backlogAgeByWorkstreamGridData,
    backlogAgeByWorkstreamColumnDefs,
    error: blAgeBandsWorkstreamsError
} = blAgeBandsWorkstreamsState;

const workstreamSummaryState = useBLWorkstreamSummaryData(queryParamsRef);
const {
    backlogOpenByWorkstreamChartXCategoriesSeries,
    backlogInOutAttrByWorkstreamChartXCategoriesSeries,
    backlogHealthByWorkstreamColumnDefs,
    backlogHealthByWorkstreamGridData,
    backlogTrendByWorkstreamColumnDefs,
    backlogTrendByWorkstreamGridData,
    workstreamSummaryGridData,
    workstreamSummaryColumnDefs,
    error: workstreamSummaryError
} = workstreamSummaryState;

// Chart configuration
const { backlogOpenByWorkstreamChartOptions, backlogInOutAttrByWorkstreamChartOptions } = useBLAnalysisChartConfigs(
    backlogOpenByWorkstreamChartXCategoriesSeries,
    backlogInOutAttrByWorkstreamChartXCategoriesSeries
);

// Combined states
const { isLoading, error } = useCombinedStates(blAgeBandsWorkstreamsState, workstreamSummaryState);
</script>

<style scoped lang="scss">
@use '../styles/backlog-insights-layout.scss';

:deep(.tabs-component-tabs) {
    padding: 0.5rem 0 1rem;
    margin: 0 1rem 1rem;
}

:deep(.tabs-component-tab-a) {
    padding-top: 0;
    padding-bottom: 0.5rem;
}
</style>
